/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import {
  APPLICATION_VIEW_OFFICE,
  SCHEDULE_TYPE_CONSTRUCTION,
  SCHEDULE_TYPE_MAINTENANCE,
  SCHEDULE_TYPE_PRE_CONSTRUCTION,
  SCHEDULE_TYPE_SALES,
} from '@gh/builder-shared-data';

import { createRoute } from '@gh/common-shared';
import { ScheduleTemplateGanttDetailsComponent } from './components/schedule-template-gantt-details/schedule-template-gantt-details.component';
import { SchedulingCalendarDetailsComponent } from './components/scheduling-calendar-details-tab/scheduling-calendar-details-tab.component';
import { SchedulingCpDetailsComponent } from './components/scheduling-cp-details/scheduling-cp-details.component';

import { SchedulingScheduleTemplateDetailsComponent } from './components/scheduling-schedule-template-details/scheduling-schedule-template-details.component';
import { SchedulingScheduleTemplateTaskDetailsComponent } from './components/scheduling-schedule-template-task-details/scheduling-schedule-template-task-details.component';
import { SchedulingScheduleTemplateListSubtabComponent } from './components/scheduling-schedule-templates-tab/scheduling-schedule-template-list-subtab/scheduling-schedule-template-list-subtab.component';
import { SchedulingScheduleTemplatesTabComponent } from './components/scheduling-schedule-templates-tab/scheduling-schedule-templates-tab.component';
import { SchedulingSupplierListTabComponent } from './components/scheduling-supplier-list-tab/scheduling-supplier-list-tab.component';
import { SchedulingTabContainerComponent } from './components/scheduling-tab-container/scheduling-tab-container.component';
import { ScheduleTemplateGanttDetailsBusinessService } from './resources/schedule-template-gantt-details-business.service';
import { SchedulingCalendarDetailsBusinessService } from './resources/scheduling-calendar-details-business.service';
import { SchedulingCpDetailsBusinessService } from './resources/scheduling-cp-details-business.service';
import { SchedulingScheduleTemplateBusinessService } from './resources/scheduling-schedule-template-business.service';
import { SchedulingScheduleTemplateTaskBusinessService } from './resources/scheduling-schedule-template-task-business.service';
import { SchedulingSupplierListBusinessService } from './resources/scheduling-supplier-list.business.service';

const routes = [
  createRoute({
    component: SchedulingTabContainerComponent,
    path: '',
    children: [
      createRoute({
        path: 'critical-path',
        component: SchedulingCpDetailsComponent,
        init: SchedulingCpDetailsBusinessService,
        leave: true,
        auth: {
          applicationView: APPLICATION_VIEW_OFFICE,
          permission: ['Setup.Schedules.CriticalPath.VIEW', 'Setup.Schedules.CriticalPath.EDIT'],
        },
      }),
      createRoute({
        path: 'schedule-templates',
        component: SchedulingScheduleTemplatesTabComponent,
        auth: {
          applicationView: APPLICATION_VIEW_OFFICE,
          permission: [
            'Setup.Schedules.ConstructionTemplates.LIST',
            'Setup.Schedules.SalesTemplates.LIST',
            'Setup.Schedules.PreConstructionTemplates.LIST',
            'Setup.Schedules.MaintenanceTemplates.LIST',
          ],
        },
        children: [
          createRoute({
            path: 'construction',
            component: SchedulingScheduleTemplateListSubtabComponent,
            init: SchedulingScheduleTemplateBusinessService,
            static: {
              type: SCHEDULE_TYPE_CONSTRUCTION,
            },
            auth: {
              applicationView: APPLICATION_VIEW_OFFICE,
              permission: [
                'Setup.Schedules.ConstructionTemplates.LIST',
              ],
            },
          }),
          createRoute({
            path: 'sales',
            component: SchedulingScheduleTemplateListSubtabComponent,
            init: SchedulingScheduleTemplateBusinessService,
            static: {
              type: SCHEDULE_TYPE_SALES,
            },
            auth: {
              applicationView: APPLICATION_VIEW_OFFICE,
              permission: [
                'Setup.Schedules.SalesTemplates.LIST',
              ],
            },
          }),
          createRoute({
            path: 'pre-construction',
            component: SchedulingScheduleTemplateListSubtabComponent,
            init: SchedulingScheduleTemplateBusinessService,
            static: {
              type: SCHEDULE_TYPE_PRE_CONSTRUCTION,
            },
            auth: {
              applicationView: APPLICATION_VIEW_OFFICE,
              permission: [
                'Setup.Schedules.PreConstructionTemplates.LIST',
              ],
            },
          }),
          createRoute({
            path: 'maintenance',
            component: SchedulingScheduleTemplateListSubtabComponent,
            init: SchedulingScheduleTemplateBusinessService,
            static: {
              type: SCHEDULE_TYPE_MAINTENANCE,
            },
            auth: {
              applicationView: APPLICATION_VIEW_OFFICE,
              permission: [
                'Setup.Schedules.MaintenanceTemplates.LIST',
              ],
            },
          }),
        ],
      }),
      createRoute({
        path: 'calendar',
        component: SchedulingCalendarDetailsComponent,
        init: SchedulingCalendarDetailsBusinessService,
        leave: true,
        auth: {
          applicationView: APPLICATION_VIEW_OFFICE,
          permission: ['Setup.Schedules.Calendar.EDIT', 'Setup.Schedules.Calendar.VIEW'],
        },
      }),
      createRoute({
        path: 'subbies-suppliers',
        component: SchedulingSupplierListTabComponent,
        init: SchedulingSupplierListBusinessService,
        leave: true,
        auth: {
          applicationView: APPLICATION_VIEW_OFFICE,
          permission: [
            'Setup.Schedules.SubContractorsSuppliers.VIEW',
            'Setup.Schedules.SubContractorsSuppliers.EDIT',
          ],
        },
      }),
    ],
  }),
  createRoute({
    path: 'schedule-templates/construction/new',
    component: SchedulingScheduleTemplateDetailsComponent,
    leave: true,
    init: {
      service: SchedulingScheduleTemplateBusinessService,
      selector: 'initScheduleTemplateDetailsView',
    },
    static: {
      type: SCHEDULE_TYPE_CONSTRUCTION,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.ConstructionTemplates.CREATE',
        'Setup.Schedules.ConstructionTemplates.EDIT',
        'Setup.Schedules.ConstructionTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/construction/:templateId',
    component: SchedulingScheduleTemplateDetailsComponent,
    leave: true,
    init: {
      service: SchedulingScheduleTemplateBusinessService,
      selector: 'initScheduleTemplateDetailsView',
    },
    static: {
      type: SCHEDULE_TYPE_CONSTRUCTION,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.ConstructionTemplates.CREATE',
        'Setup.Schedules.ConstructionTemplates.EDIT',
        'Setup.Schedules.ConstructionTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/sales/new',
    component: SchedulingScheduleTemplateDetailsComponent,
    leave: true,
    init: {
      service: SchedulingScheduleTemplateBusinessService,
      selector: 'initScheduleTemplateDetailsView',
    },
    static: {
      type: SCHEDULE_TYPE_SALES,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.SalesTemplates.CREATE',
        'Setup.Schedules.SalesTemplates.EDIT',
        'Setup.Schedules.SalesTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/sales/:templateId',
    component: SchedulingScheduleTemplateDetailsComponent,
    leave: true,
    init: {
      service: SchedulingScheduleTemplateBusinessService,
      selector: 'initScheduleTemplateDetailsView',
    },
    static: {
      type: SCHEDULE_TYPE_SALES,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.SalesTemplates.CREATE',
        'Setup.Schedules.SalesTemplates.EDIT',
        'Setup.Schedules.SalesTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/pre-construction/new',
    component: SchedulingScheduleTemplateDetailsComponent,
    leave: true,
    init: {
      service: SchedulingScheduleTemplateBusinessService,
      selector: 'initScheduleTemplateDetailsView',
    },
    static: {
      type: SCHEDULE_TYPE_PRE_CONSTRUCTION,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.PreConstructionTemplates.CREATE',
        'Setup.Schedules.PreConstructionTemplates.EDIT',
        'Setup.Schedules.PreConstructionTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/pre-construction/:templateId',
    component: SchedulingScheduleTemplateDetailsComponent,
    leave: true,
    init: {
      service: SchedulingScheduleTemplateBusinessService,
      selector: 'initScheduleTemplateDetailsView',
    },
    static: {
      type: SCHEDULE_TYPE_PRE_CONSTRUCTION,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.PreConstructionTemplates.CREATE',
        'Setup.Schedules.PreConstructionTemplates.EDIT',
        'Setup.Schedules.PreConstructionTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/maintenance/new',
    component: SchedulingScheduleTemplateDetailsComponent,
    leave: true,
    init: {
      service: SchedulingScheduleTemplateBusinessService,
      selector: 'initScheduleTemplateDetailsView',
    },
    static: {
      type: SCHEDULE_TYPE_MAINTENANCE,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.MaintenanceTemplates.CREATE',
        'Setup.Schedules.MaintenanceTemplates.EDIT',
        'Setup.Schedules.MaintenanceTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/maintenance/:templateId',
    component: SchedulingScheduleTemplateDetailsComponent,
    leave: true,
    init: {
      service: SchedulingScheduleTemplateBusinessService,
      selector: 'initScheduleTemplateDetailsView',
    },
    static: {
      type: SCHEDULE_TYPE_MAINTENANCE,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.MaintenanceTemplates.CREATE',
        'Setup.Schedules.MaintenanceTemplates.EDIT',
        'Setup.Schedules.MaintenanceTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/construction/:templateId/tasks/:taskId',
    component: SchedulingScheduleTemplateTaskDetailsComponent,
    init: SchedulingScheduleTemplateTaskBusinessService,
    leave: true,
    static: {
      type: SCHEDULE_TYPE_CONSTRUCTION,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.ConstructionTemplates.CREATE',
        'Setup.Schedules.ConstructionTemplates.EDIT',
        'Setup.Schedules.ConstructionTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/sales/:templateId/tasks/:taskId',
    component: SchedulingScheduleTemplateTaskDetailsComponent,
    init: SchedulingScheduleTemplateTaskBusinessService,
    leave: true,
    static: {
      type: SCHEDULE_TYPE_SALES,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.SalesTemplates.CREATE',
        'Setup.Schedules.SalesTemplates.EDIT',
        'Setup.Schedules.SalesTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/pre-construction/:templateId/tasks/:taskId',
    component: SchedulingScheduleTemplateTaskDetailsComponent,
    init: SchedulingScheduleTemplateTaskBusinessService,
    leave: true,
    static: {
      type: SCHEDULE_TYPE_PRE_CONSTRUCTION,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.PreConstructionTemplates.CREATE',
        'Setup.Schedules.PreConstructionTemplates.EDIT',
        'Setup.Schedules.PreConstructionTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/maintenance/:templateId/tasks/:taskId',
    component: SchedulingScheduleTemplateTaskDetailsComponent,
    init: SchedulingScheduleTemplateTaskBusinessService,
    leave: true,
    static: {
      type: SCHEDULE_TYPE_MAINTENANCE,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.MaintenanceTemplates.CREATE',
        'Setup.Schedules.MaintenanceTemplates.EDIT',
        'Setup.Schedules.MaintenanceTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/construction/:templateId/gantt-chart',
    component: ScheduleTemplateGanttDetailsComponent,
    leave: true,
    init: {
      service: ScheduleTemplateGanttDetailsBusinessService,
      selector: 'initView',
    },
    static: {
      type: SCHEDULE_TYPE_CONSTRUCTION,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.ConstructionTemplates.EDIT',
        'Setup.Schedules.ConstructionTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/pre-construction/:templateId/gantt-chart',
    component: ScheduleTemplateGanttDetailsComponent,
    leave: true,
    init: {
      service: ScheduleTemplateGanttDetailsBusinessService,
      selector: 'initView',
    },
    static: {
      type: SCHEDULE_TYPE_PRE_CONSTRUCTION,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.PreConstructionTemplates.EDIT',
        'Setup.Schedules.PreConstructionTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/maintenance/:templateId/gantt-chart',
    component: ScheduleTemplateGanttDetailsComponent,
    leave: true,
    init: {
      service: ScheduleTemplateGanttDetailsBusinessService,
      selector: 'initView',
    },
    static: {
      type: SCHEDULE_TYPE_MAINTENANCE,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.MaintenanceTemplates.EDIT',
        'Setup.Schedules.MaintenanceTemplates.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'schedule-templates/sales/:templateId/gantt-chart',
    component: ScheduleTemplateGanttDetailsComponent,
    leave: true,
    init: {
      service: ScheduleTemplateGanttDetailsBusinessService,
      selector: 'initView',
    },
    static: {
      type: SCHEDULE_TYPE_SALES,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Setup.Schedules.SalesTemplates.EDIT',
        'Setup.Schedules.SalesTemplates.VIEW',
      ],
    },
  }),
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class BuilderSetupSchedulingRoutingModule {
}
